// Vendor Libs
import React from 'react';
import PropTypes from 'prop-types';
import {cloneDeep, filter, findIndex, get, isEmpty, map, slice} from 'lodash';

// Components
import Button from '../core/button';
import Checkbox from '../core/checkbox';
import MassActionConfirm from './mass-action-confirm';
import MassActionResponse from './mass-action-response';
import PanelAction from '../core/panel-action';
import Permissions from '../core/permissions';
import SearchPagination from '../app/search-pagination';
import SearchTableWithSort from '../app/search-tablewithsort';

// Containers
import MassAction from '../../containers/mass-action';
import withPermissions from '../../containers/with-permissions';

// Styles
import './mass-action-table.scss';

function MassActionTable({
    actionIdKey,
    massActionName,
    massActionOptions = [],
    paginationProps,
    responseTablewithsortProps: {columnConfig} = {},
    serviceKey,
    showMassActionsPanelAction = true,
    tableWithSortProps,
}) {
    const getColumnConfig = (massAction) => {
        const {active} = massAction;
        const {columnConfig} = cloneDeep(tableWithSortProps);

        if (active) {
            columnConfig.push({
                dataKey: actionIdKey,
                renderRowCallback: (dataRow, key) => renderCheckbox(dataRow, key, massAction),
                renderTitleCallback: (column, key) => renderTableTitle(column, key, massAction),
                tooltip: 'Select All',
                width: '3%',
            });

            return map(columnConfig, (column) => ({
                ...column,
                width: column.massActionWidth || column.width,
            }));
        }

        return columnConfig;
    };

    const getTableProps = (massAction) => ({
        ...tableWithSortProps,
        columnConfig: getColumnConfig(massAction),
    });

    const getResponseProps = (massAction) => {
        const {childFormName, formData, label, username} = massAction;
        const {[childFormName]: childFormData = {}} = formData;

        return {
            columnConfig,
            formData: childFormData,
            label,
            onReset: () => handleResponseReset(massAction),
            username,
        };
    };

    const handleActionClick = (_event, childFormName, label, showMessage, massAction) => {
        const {massActionSetChildFormData} = massAction;

        massActionSetChildFormData({childFormName, label, showMessage});
    };

    const handleCheckboxChange = (event, massAction) => {
        const {value: fieldValue, checked} = event.target;
        const {
            hasPermission,
            lastId,
            massActionAddTargets,
            massActionSetLastId,
            massActionSetSelectAll,
            searchData,
            selectAll,
        } = massAction;

        if (checked && fieldValue) {
            if (fieldValue !== lastId) {
                if (get(event.nativeEvent, 'shiftKey') && lastId) {
                    event.preventDefault && event.preventDefault();

                    const startIndex = findIndex(searchData, [actionIdKey, lastId]);
                    const endIndex = findIndex(searchData, [actionIdKey, fieldValue]);

                    if (startIndex > -1 && endIndex > -1) {
                        massActionAddTargets(
                            filter(
                                slice(searchData, Math.min(startIndex, endIndex), Math.max(startIndex, endIndex)),
                                (record) => hasPermission(record)
                            ),
                        );
                    }
                }

                massActionSetLastId(fieldValue);
            }
        } else if (lastId !== null) {
            massActionSetLastId(null);
        }

        if (!checked && selectAll) {
            massActionSetSelectAll(false);
        }
    };

    const handleMassActionCancelClick = (event, massAction) => {
        event && event.preventDefault && event.preventDefault();

        const {massActionSetActive} = massAction;

        massActionSetActive(false);
    };

    const handlePanelActionClick = (massAction) => {
        const {active, massActionSetActive} = massAction;

        massActionSetActive(!active);
    };

    const handleResponseReset = (massAction) => {
        const {massActionSetChildFormData} = massAction;

        massActionSetChildFormData({});
    };

    const handleSelectAllChange = (event, {massActionClearTargets, massActionSetSelectAll}) => {
        event.persist();

        if (!event.target.checked) {
            massActionClearTargets();
        }

        massActionSetSelectAll(event.target.checked);
    };

    const submitForm = (massAction) => {
        const {massActionSubmit} = massAction;

        massActionSubmit();
    };

    const renderCheckbox = (dataRow, key, massAction) => {
        const serviceName = get(dataRow, 'service.serviceName');
        const recordId = dataRow[actionIdKey];
        const {targetIds = []} = massAction;
        const checkboxProps = {
            className: 'mass-action-table--checkbox',
            formValues: targetIds,
            key,
            lockingServiceKey: serviceKey,
            name: 'targets[]',
            onChange: (event) => handleCheckboxChange(event, massAction),
            value: recordId,
        };
        const permissionsProps = {
            accessRequirement: 'write',
            elementService: serviceName,
            lockAccess: true,
            recordId,
            requiresServiceAccess: !!serviceName,
            serviceKey,
        };

        return (
            (
                <Permissions {...permissionsProps}>
                    <Checkbox {...checkboxProps} />
                </Permissions>
            ) || null
        );
    };

    const renderFormResponse = (massAction) => {
        const {childFormName, label, submitted, showMessage, targetIds = []} = massAction;

        return childFormName && showMessage ? (
            submitted ? (
                <MassActionResponse {...getResponseProps(massAction)} />
            ) : (
                <MassActionConfirm
                    actionsCount={targetIds.length}
                    label={label}
                    onReset={() => handleResponseReset(massAction)}
                    onSubmit={() => submitForm(massAction)}
                />
            )
        ) : null;
    };

    const renderMassActionOptions = (massAction) => {
        const {targetIds} = massAction;
        const disabled = isEmpty(targetIds);
        const actionButtons = map(
            massActionOptions,
            ({adminOnly, allowRoles, formName: childFormName, label, showMessage = true}, key) => {
                const buttonProps = {
                    className: 'mass-action-table--button',
                    disabled,
                    key: !adminOnly ? key : undefined,
                    onClick: (event) => handleActionClick(event, childFormName, label, showMessage, massAction),
                    value: childFormName,
                };

                return (
                    <Permissions adminOnly={adminOnly} allowRoles={allowRoles} key={key}>
                        <Button {...buttonProps}>{label}</Button>
                    </Permissions>
                );
            }
        );

        return (
            <div className="mass-action-table--options">
                {actionButtons}
                <Button
                    className="mass-action-table--button"
                    onClick={(event) => handleMassActionCancelClick(event, massAction)}
                >
                    Cancel Mass Actions
                </Button>
            </div>
        );
    };

    const renderPagination = (massAction) => {
        const {active, searchData} = massAction;

        return !isEmpty(searchData) ? (
            <div className="mass-action-table--pagination-options">
                {paginationProps ? <SearchPagination {...paginationProps} inline={true} /> : null}
                {active ? renderMassActionOptions(massAction) : null}
            </div>
        ) : null;
    };

    const renderPanelAction = (massAction) => {
        const panelActionProps = {
            hotkey: 'm',
            icon: 'check-square-o',
            onClick: () => handlePanelActionClick(massAction),
            title: 'Mass Actions',
            weight: 30,
        };

        return showMassActionsPanelAction ? (
            <Permissions accessRequirement="write">
                <PanelAction {...panelActionProps} />
            </Permissions>
        ) : null;
    };

    const renderTableTitle = (_column, key, massAction) => {
        const {selectAll} = massAction;
        const inputProps = {
            className: 'mass-action-table--checkbox-title',
            checked: selectAll,
            key,
            name: 'massActionSelectAll',
            onChange: (event) => handleSelectAllChange(event, massAction),
            title: 'Select All',
            type: 'checkbox',
            value: 'selectAll',
        };

        return <input {...inputProps} />;
    };

    return (
        <MassAction massActionName={massActionName}>
            {(massAction) => {
                const {searchData} = massAction;

                return (
                    <div className="mass-action-table">
                        {!isEmpty(searchData) ? renderPagination(massAction) : null}
                        {renderPanelAction(massAction)}
                        <SearchTableWithSort {...getTableProps(massAction)} />
                        {renderFormResponse(massAction)}
                    </div>
                );
            }}
        </MassAction>
    );
}

MassActionTable.propTypes = {
    actionIdKey: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
    hasAccessByService: PropTypes.func.isRequired,
    hasLockingAccess: PropTypes.func.isRequired,
    massActionName: PropTypes.string.isRequired,
    massActionOptions: PropTypes.array,
    paginationProps: PropTypes.object,
    responseTablewithsortProps: PropTypes.object.isRequired,
    serviceKey: PropTypes.string.isRequired,
    showMassActionsPanelAction: PropTypes.bool,
    tableWithSortProps: PropTypes.object,
};

export default withPermissions(MassActionTable);
